import dayjs from "dayjs";
import { useMemo } from "react";
import {
  TransactionType,
  TransactionTypeLog,
  TransactionTypeLogType,
} from "@lib/types";
import { Typography } from "antd";
import { TimelineItemProps } from "antd/lib";
import { FormattedMessage, useIntl } from "react-intl";
import {
  MdVisibility,
  MdAdd,
  MdEdit,
  MdDelete,
  MdContentCopy,
  MdError,
  MdAttachFile,
} from "react-icons/md";
import { AppCard, AppJson, AppTimeline } from "@lib/components/AppComponents";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import {
  PaymentHistoryState,
  PaymentHistoryStatus,
} from "@lib/components/MiniComponents/Others";

type Props = {
  transaction?: TransactionType;
};

export default function TransactionLogs(props: Props) {
  const { transaction } = props;
  const { messages } = useIntl();

  const getLogTypeConfig = (
    type: TransactionTypeLogType,
    props?: Record<string, any>,
  ) => {
    const configs = {
      [TransactionTypeLogType.Viewed]: {
        color: "rgb(24, 144, 255)",
        icon: <MdVisibility {...props} />,
      },
      [TransactionTypeLogType.Created]: {
        color: "rgb(17, 163, 102)",
        icon: <MdAdd {...props} />,
      },
      [TransactionTypeLogType.Updated]: {
        color: "rgb(255, 187, 7)",
        icon: <MdEdit {...props} />,
      },
      [TransactionTypeLogType.Copied]: {
        color: "rgb(114, 46, 209)",
        icon: <MdContentCopy {...props} />,
      },
      [TransactionTypeLogType.Deleted]: {
        color: "rgb(241, 0, 0)",
        icon: <MdDelete {...props} />,
      },
      [TransactionTypeLogType.Failed]: {
        color: "rgb(218, 86, 25)",
        icon: <MdError {...props} />,
      },
      [TransactionTypeLogType.Document]: {
        color: "rgba(48, 96, 255, 0.86)",
        icon: <MdAttachFile {...props} />,
      },
    };
    return configs[type] || configs[TransactionTypeLogType.Viewed];
  };

  const getLogMessage = (log: TransactionTypeLog) => {
    switch (log.type) {
      case TransactionTypeLogType.Updated: {
        const changedFields = log?.extraData?.changedFields;
        if (changedFields?.status) {
          return <PaymentHistoryStatus status={changedFields?.status} />;
        }
        if (changedFields?.state) {
          return <PaymentHistoryState state={changedFields?.state} />;
        }
        return <FormattedMessage id="common.updatedItem" />;
      }
      case TransactionTypeLogType.Copied:
        return <FormattedMessage id="common.copiedItem" />;
      case TransactionTypeLogType.Created:
        return <FormattedMessage id="common.createdItem" />;
      case TransactionTypeLogType.Deleted:
        return <FormattedMessage id="common.deletedItem" />;
      case TransactionTypeLogType.Viewed:
        return <FormattedMessage id="common.viewedItem" />;
      case TransactionTypeLogType.Failed:
        return <FormattedMessage id="common.failedItem" />;
      case TransactionTypeLogType.Document:
        return <FormattedMessage id="common.fileUploadedItem" />;
      default:
        return (messages[log.comment] as string) || log.comment;
    }
  };

  const items: TimelineItemProps[] = useMemo(() => {
    return (transaction?.logs || [])
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )
      .map((log) => ({
        label: (
          <StyledFlex style={{ flexDirection: "column", alignItems: "end" }}>
            <Typography.Text style={{ fontSize: 14 }}>
              {dayjs(log.createdAt).format("DD/MM/YYYY HH:mm")}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {log?.manager?.firstName} {log?.manager?.lastName}
            </Typography.Text>
          </StyledFlex>
        ),
        color: getLogTypeConfig(log.type).color,
        dot: getLogTypeConfig(log.type, { size: 16 }).icon,
        children: (
          <AppCard
            borderFull
            collapse={true}
            collapsed
            collapseIconSide="left"
            title={getLogMessage(log)}
            // extra={
            //   <AppTag
            //     color={getLogTypeConfig(log.type).color}
            //     style={{
            //       position: "absolute",
            //       right: 0,
            //       top: 0,
            //     }}
            //   >
            //     {getLogMessage(log)}
            //   </AppTag>
            // }
          >
            <div
              style={{
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              {log?.comment || Object.keys(log?.extraData || {}).length > 0 ? (
                <>
                  {log?.comment && (
                    <div
                      dangerouslySetInnerHTML={{ __html: log?.comment }}
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    />
                  )}
                  {Object.keys(log?.extraData || {}).length > 0 && (
                    <AppJson src={log?.extraData || {}} />
                  )}
                </>
              ) : (
                "No comment or extra data"
              )}
            </div>
          </AppCard>
        ),
      }));
  }, [transaction]);

  return (
    <div style={{ padding: 16 }}>
      <AppTimeline mode="left" items={items} leftWidth="20%" />
    </div>
  );
}
